import React, { useContext } from 'react';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import Head from 'next/head';

import { BrandingContext } from '../../context/branding.context';
import { UserContext } from '../../context/user.context';
import { useNProgress } from '../../hooks';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import ErrorBoundary from '../errors/errorBoundary/errorBoundary';
import { ConsentBanner } from '../users/consentBanner';
import { Header } from './header';

import styles from './layout.module.scss';

const Footer = dynamic(() => import('./footer').then(mod => mod.Footer));

/**
 * IProps Interface
 */
interface IProps {
  /** The title of the page, which will be displayed in the browser tab. */
  title?: string;
  /**  Should the body have a background fill. */
  fill?: boolean;
  /** Optional classname for the layout */
  className?: string;
}

/**
 * Layout Component
 * A reusable layout component for a Next.js app, including a header, main content area, and footer.
 * @param {React.PropsWithChildren<IProps>} props - Props for the Layout component
 * @returns {React.ReactElement} JSX element for the layout
 */
export const Layout: React.FC<React.PropsWithChildren<IProps>> = ({ children, title, fill, className }) => {
  const { windowTitle } = useContext(BrandingContext);
  const { authenticated } = useContext(UserContext);
  useNProgress();

  const { hasFeatureFlag } = useFeatureFlag();
  const isUserAnalyticsEnabled = hasFeatureFlag('seccl-19639-user-analytics');

  return (
    <ErrorBoundary>
      <Head>
        <title>{title ? `${title} | ${windowTitle}` : windowTitle}</title>
      </Head>
      <div className={cn(styles.layout, className)}>
        {authenticated && <Header />}
        <main className={styles.main} data-fill={fill}>
          {children}
        </main>
        {authenticated && <Footer />}
        {authenticated && isUserAnalyticsEnabled && <ConsentBanner />}
      </div>
    </ErrorBoundary>
  );
};
